/**
 * Annotation Editor - Main Component
 * 
 * Full-screen editor for adding annotations to the traffic control plan.
 * Opens after the print area (bounding box) is selected.
 * 
 * Layout:
 * - Left: Annotation toolbar with tools and templates
 * - Center: Page canvas showing captured map with draggable annotations
 * - Right: Properties panel for editing selected annotation
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  Divider,
  Tooltip,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import SaveAsIcon from '@mui/icons-material/SaveAs';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { AnnotationToolbar } from './AnnotationToolbar';
import { PageCanvas, PageCanvasHandle, BORDER_PADDING_PERCENT } from './PageCanvas';
import { AnnotationPropertiesPanel } from './AnnotationPropertiesPanel';
import type {
  PageAnnotation,
  AnnotationType,
  AnnotationTemplate,
  AnnotationEditorState,
  TitleBoxAnnotation,
  NorthArrowAnnotation,
  LegendAnnotation,
  ManifestAnnotation,
  NotesAnnotation,
  ScaleBarAnnotation,
  LegendEntry,
  ManifestEntry,
} from '../../types/annotations';
import {
  PAPER_DIMENSIONS,
  DEFAULT_TITLE_BOX,
  DEFAULT_NORTH_ARROW,
  DEFAULT_LEGEND,
  DEFAULT_MANIFEST,
  DEFAULT_NOTES,
  DEFAULT_SCALE_BAR,
  BUILT_IN_TEMPLATES,
  generateAnnotationId,
} from '../../types/annotations';

interface AnnotationEditorProps {
  open: boolean;
  onClose: () => void;
  onExport: (annotations: PageAnnotation[], capturedAnnotatedCanvas: string | null) => void;
  onSaveProject?: () => void;  // Save current project
  onSaveTemplate?: (name: string, description: string, annotations: PageAnnotation[]) => Promise<void>;
  capturedCanvas: string | null;  // base64 data URL of the map
  paperSize: 'A4' | 'A3' | 'A2' | 'A1' | 'A0';
  orientation: 'portrait' | 'landscape';
  // Data for auto-populating legend and manifest
  signs?: Array<{ code: string; description?: string; category?: string }>;
  delineators?: Array<{ deviceType: string; count: number }>;
  workzones?: Array<{ id: string; area?: number }>;
  // When true, silently capture the canvas with no annotations and immediately export
  autoExport?: boolean;
}

export const AnnotationEditor: React.FC<AnnotationEditorProps> = ({
  open,
  onClose,
  onExport,
  onSaveProject,
  onSaveTemplate,
  capturedCanvas,
  paperSize,
  orientation,
  signs = [],
  delineators = [],
  workzones = [],
  autoExport = false,
}) => {
  // Refs
  const pageCanvasRef = useRef<PageCanvasHandle>(null);
  
  // Editor state
  const [annotations, setAnnotations] = useState<PageAnnotation[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [zoom, setZoom] = useState(0.6);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isExporting, setIsExporting] = useState(false);
  
  // History for undo/redo
  const [history, setHistory] = useState<PageAnnotation[][]>([[]]);
  const [historyIndex, setHistoryIndex] = useState(0);
  
  // Save menu state
  const [saveMenuAnchor, setSaveMenuAnchor] = useState<null | HTMLElement>(null);
  const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  
  // Get paper dimensions
  const dimensions = PAPER_DIMENSIONS[paperSize][orientation];
  
  // Selected annotation
  const selectedAnnotation = annotations.find(a => a.id === selectedId) || null;

  // Generate legend entries from signs
  const generateLegendEntries = useCallback((): LegendEntry[] => {
    const signCounts = new Map<string, { code: string; label: string; count: number; category?: string }>();
    
    signs.forEach(sign => {
      const key = sign.code;
      if (signCounts.has(key)) {
        signCounts.get(key)!.count++;
      } else {
        signCounts.set(key, {
          code: sign.code,
          label: sign.description || sign.code,
          count: 1,
          category: sign.category,
        });
      }
    });

    // Add delineators
    delineators.forEach(d => {
      const key = `delineator-${d.deviceType}`;
      const label = d.deviceType === 'cone' ? 'Traffic Cone' : 
                    d.deviceType === 'yellow_dot' ? 'Traffic Dot' : 
                    d.deviceType.charAt(0).toUpperCase() + d.deviceType.slice(1);
      signCounts.set(key, {
        code: d.deviceType.toUpperCase(),
        label,
        count: d.count,
        category: 'Delineators',
      });
    });

    return Array.from(signCounts.values()).map(entry => ({
      icon: '',
      label: entry.label,
      code: entry.code,
      count: entry.count,
      category: entry.category,
    }));
  }, [signs, delineators]);

  // Generate manifest entries
  const generateManifestEntries = useCallback((): ManifestEntry[] => {
    const entries: ManifestEntry[] = [];
    
    // Signs
    const signCounts = new Map<string, number>();
    signs.forEach(sign => {
      signCounts.set(sign.code, (signCounts.get(sign.code) || 0) + 1);
    });
    signCounts.forEach((count, code) => {
      const sign = signs.find(s => s.code === code);
      entries.push({
        item: `${sign?.description || code} (${code})`,
        quantity: count,
        unit: 'ea',
        category: 'Signs',
      });
    });

    // Delineators
    delineators.forEach(d => {
      const label = d.deviceType === 'cone' ? 'Traffic Cone (750mm)' : 
                    d.deviceType === 'yellow_dot' ? 'Traffic Yellow Dot' : 
                    d.deviceType.charAt(0).toUpperCase() + d.deviceType.slice(1);
      entries.push({
        item: label,
        quantity: d.count,
        unit: 'ea',
        category: 'Delineators',
      });
    });

    return entries;
  }, [signs, delineators]);

  // Title box height (matches backend _TITLE_BOX_HEIGHT ratio - about 10% of page height)
  // Border padding in page coordinates (not scaled by zoom - zoom is applied in PageCanvas)
  const borderPad = BORDER_PADDING_PERCENT * Math.min(dimensions.width, dimensions.height);
  const topPad = borderPad * 1.8; // Extra top padding for branding text
  const contentWidth = dimensions.width - borderPad * 2;
  const contentHeight = dimensions.height - topPad - borderPad;
  const TITLE_BOX_HEIGHT = Math.round(contentHeight * 0.12);

  // Keep title box position/size in sync with page dimensions
  // Title box sits at the bottom of the content area (inside the border)
  useEffect(() => {
    setAnnotations(prev => prev.map(ann => {
      if (ann.type === 'title-box') {
        return {
          ...ann,
          position: { x: borderPad, y: dimensions.height - borderPad - TITLE_BOX_HEIGHT },
          width: contentWidth,
          height: TITLE_BOX_HEIGHT,
        };
      }
      return ann;
    }));
  }, [dimensions, TITLE_BOX_HEIGHT, borderPad, contentWidth]);
  
  // Default positions for each annotation type
  const getDefaultPosition = useCallback((type: AnnotationType): { x: number; y: number } => {
    const margin = 20;
    const titleBoxBottom = dimensions.height - borderPad - TITLE_BOX_HEIGHT;
    switch (type) {
      case 'title-box':
        // Fixed position at bottom of content area, inside the border
        return { x: borderPad, y: titleBoxBottom };
      case 'north-arrow':
        // Top right corner
        return { x: dimensions.width - DEFAULT_NORTH_ARROW.width - margin, y: margin };
      case 'legend':
        // Bottom left (above title box area)
        return { x: margin, y: titleBoxBottom - DEFAULT_LEGEND.height - margin };
      case 'manifest':
        // Bottom right (above title box area)
        return { x: dimensions.width - DEFAULT_MANIFEST.width - margin, y: titleBoxBottom - DEFAULT_MANIFEST.height - margin };
      case 'notes':
        // Left side, middle
        return { x: margin, y: dimensions.height / 2 - DEFAULT_NOTES.height / 2 };
      case 'scale-bar':
        // Bottom center (above title box area)
        return { x: dimensions.width / 2 - DEFAULT_SCALE_BAR.width / 2, y: titleBoxBottom - DEFAULT_SCALE_BAR.height - margin };
      default:
        return { x: margin, y: margin };
    }
  }, [dimensions, TITLE_BOX_HEIGHT, borderPad]);

  // Handle tool selection - immediately add annotation at default position
  const handleToolSelect = useCallback((type: AnnotationType | null) => {
    if (!type) return;
    
    const id = generateAnnotationId();
    const position = getDefaultPosition(type);
    let newAnnotation: PageAnnotation;

    switch (type) {
      case 'title-box':
        // Title box spans content width (inside border) with fixed height at bottom
        newAnnotation = {
          ...DEFAULT_TITLE_BOX,
          id,
          position,
          width: contentWidth,
          height: TITLE_BOX_HEIGHT,
        } as TitleBoxAnnotation;
        break;
      case 'north-arrow':
        newAnnotation = { ...DEFAULT_NORTH_ARROW, id, position } as NorthArrowAnnotation;
        break;
      case 'legend':
        newAnnotation = { 
          ...DEFAULT_LEGEND, 
          id, 
          position,
          entries: generateLegendEntries(),
        } as LegendAnnotation;
        break;
      case 'manifest':
        newAnnotation = { 
          ...DEFAULT_MANIFEST, 
          id, 
          position,
          entries: generateManifestEntries(),
        } as ManifestAnnotation;
        break;
      case 'notes':
        newAnnotation = { ...DEFAULT_NOTES, id, position } as NotesAnnotation;
        break;
      case 'scale-bar':
        newAnnotation = { ...DEFAULT_SCALE_BAR, id, position } as ScaleBarAnnotation;
        break;
      default:
        return;
    }

    setAnnotations(prev => [...prev, newAnnotation]);
    setSelectedId(id);
    
    // Add to history
    saveToHistory([...annotations, newAnnotation]);
  }, [annotations, dimensions, TITLE_BOX_HEIGHT, contentWidth, generateLegendEntries, generateManifestEntries, getDefaultPosition]);

  // Add annotation to canvas (for manual placement if needed)
  const handleAddAnnotation = useCallback((type: AnnotationType, position: { x: number; y: number }) => {
    const id = generateAnnotationId();
    let newAnnotation: PageAnnotation;

    switch (type) {
      case 'title-box':
        // Title box always spans full width at the bottom
        newAnnotation = { 
          ...DEFAULT_TITLE_BOX, 
          id, 
          position: { x: 0, y: dimensions.height - TITLE_BOX_HEIGHT },
          width: dimensions.width,
          height: TITLE_BOX_HEIGHT,
        } as TitleBoxAnnotation;
        break;
      case 'north-arrow':
        newAnnotation = { ...DEFAULT_NORTH_ARROW, id, position } as NorthArrowAnnotation;
        break;
      case 'legend':
        newAnnotation = { 
          ...DEFAULT_LEGEND, 
          id, 
          position,
          entries: generateLegendEntries(),
        } as LegendAnnotation;
        break;
      case 'manifest':
        newAnnotation = { 
          ...DEFAULT_MANIFEST, 
          id, 
          position,
          entries: generateManifestEntries(),
        } as ManifestAnnotation;
        break;
      case 'notes':
        newAnnotation = { ...DEFAULT_NOTES, id, position } as NotesAnnotation;
        break;
      case 'scale-bar':
        newAnnotation = { ...DEFAULT_SCALE_BAR, id, position } as ScaleBarAnnotation;
        break;
      default:
        return;
    }

    setAnnotations(prev => [...prev, newAnnotation]);
    setSelectedId(id);

    // Add to history
    saveToHistory([...annotations, newAnnotation]);
  }, [annotations, dimensions, TITLE_BOX_HEIGHT, generateLegendEntries, generateManifestEntries]);

  // Update annotation
  const handleUpdateAnnotation = useCallback((id: string, updates: Partial<PageAnnotation>) => {
    setAnnotations(prev => 
      prev.map(ann => ann.id === id ? { ...ann, ...updates } as PageAnnotation : ann)
    );
  }, []);

  // Move annotation (title box is fixed, cannot be moved)
  const handleMoveAnnotation = useCallback((id: string, position: { x: number; y: number }) => {
    setAnnotations(prev => 
      prev.map(ann => {
        if (ann.id === id) {
          // Title box has fixed position, don't allow moving
          if (ann.type === 'title-box') {
            return ann;
          }
          return { ...ann, position };
        }
        return ann;
      })
    );
  }, []);

  // Resize annotation (title box has fixed size, cannot be resized)
  const handleResizeAnnotation = useCallback((id: string, width: number, height: number) => {
    setAnnotations(prev => 
      prev.map(ann => {
        if (ann.id === id) {
          // Title box has fixed dimensions, don't allow resizing
          if (ann.type === 'title-box') {
            return ann;
          }
          return { ...ann, width, height };
        }
        return ann;
      })
    );
  }, []);

  // Delete annotation
  const handleDeleteAnnotation = useCallback((id: string) => {
    setAnnotations(prev => prev.filter(ann => ann.id !== id));
    if (selectedId === id) {
      setSelectedId(null);
    }
    saveToHistory(annotations.filter(ann => ann.id !== id));
  }, [annotations, selectedId]);

  // Apply template
  const handleApplyTemplate = useCallback((template: AnnotationTemplate) => {
    // Deep clone and regenerate IDs
    const newAnnotations = template.annotations.map(ann => {
      const newAnn = { ...ann, id: generateAnnotationId() };
      
      // Auto-populate legend and manifest
      if (newAnn.type === 'legend') {
        (newAnn as LegendAnnotation).entries = generateLegendEntries();
      } else if (newAnn.type === 'manifest') {
        (newAnn as ManifestAnnotation).entries = generateManifestEntries();
      }
      
      return newAnn;
    });
    
    setAnnotations(newAnnotations);
    setSelectedId(null);
    saveToHistory(newAnnotations);
  }, [generateLegendEntries, generateManifestEntries]);

  // History management
  const saveToHistory = useCallback((newAnnotations: PageAnnotation[]) => {
    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(newAnnotations);
      return newHistory.slice(-50); // Keep last 50 states
    });
    setHistoryIndex(prev => Math.min(prev + 1, 49));
  }, [historyIndex]);

  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      setHistoryIndex(prev => prev - 1);
      setAnnotations(history[historyIndex - 1]);
    }
  }, [history, historyIndex]);

  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(prev => prev + 1);
      setAnnotations(history[historyIndex + 1]);
    }
  }, [history, historyIndex]);

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    setZoom(prev => Math.min(prev + 0.1, 2));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom(prev => Math.max(prev - 0.1, 0.25));
  }, []);

  const handleFitToScreen = useCallback(() => {
    // Calculate zoom to fit page in viewport
    const viewportWidth = window.innerWidth - 520; // minus sidebars
    const viewportHeight = window.innerHeight - 100; // minus header
    const scaleX = viewportWidth / dimensions.width;
    const scaleY = viewportHeight / dimensions.height;
    setZoom(Math.min(scaleX, scaleY, 1) * 0.9);
    setPanOffset({ x: 0, y: 0 });
  }, [dimensions]);

  // Handle export - captures the full page canvas with annotations
  const handleExport = useCallback(async () => {
    if (isExporting) return;
    
    setIsExporting(true);
    
    try {
      // Deselect any annotation to remove selection outlines
      setSelectedId(null);
      
      // Wait a frame for the UI to update
      await new Promise(resolve => setTimeout(resolve, 50));
      
      // Capture the page canvas with annotations
      let capturedAnnotatedCanvas: string | null = null;
      if (pageCanvasRef.current) {
        capturedAnnotatedCanvas = await pageCanvasRef.current.captureCanvas();
        console.log('Captured annotated canvas:', capturedAnnotatedCanvas ? 'success' : 'failed');
      }
      
      onExport(annotations, capturedAnnotatedCanvas);
    } catch (error) {
      console.error('Error during export capture:', error);
      // Still call onExport even if capture fails
      onExport(annotations, null);
    } finally {
      setIsExporting(false);
    }
  }, [annotations, onExport, isExporting]);

  // Save project handler
  const handleSaveProject = useCallback(() => {
    setSaveMenuAnchor(null);
    onSaveProject?.();
  }, [onSaveProject]);

  // Save template handler
  const handleSaveTemplate = useCallback(async () => {
    if (!templateName.trim() || !onSaveTemplate) return;
    
    setSavingTemplate(true);
    try {
      await onSaveTemplate(templateName.trim(), templateDescription.trim(), annotations);
      setShowSaveTemplateDialog(false);
      setTemplateName('');
      setTemplateDescription('');
    } catch (error) {
      console.error('Failed to save template:', error);
    } finally {
      setSavingTemplate(false);
    }
  }, [templateName, templateDescription, annotations, onSaveTemplate]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open) return;
      
      if (e.key === 'Escape') {
        if (selectedId) {
          setSelectedId(null);
        } else {
          onClose();
        }
      }
      
      if (e.key === 'Delete' && selectedId) {
        handleDeleteAnnotation(selectedId);
      }
      
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        if (e.shiftKey) {
          handleRedo();
        } else {
          handleUndo();
        }
        e.preventDefault();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, selectedId, onClose, handleDeleteAnnotation, handleUndo, handleRedo]);

  // Auto-export: silently capture canvas with no annotations and immediately export
  const autoExportTriggered = useRef(false);
  useEffect(() => {
    if (!open || !autoExport || autoExportTriggered.current) return;
    // Wait for PageCanvas to mount and render the map image
    const timer = setTimeout(async () => {
      autoExportTriggered.current = true;
      let captured: string | null = null;
      if (pageCanvasRef.current) {
        captured = await pageCanvasRef.current.captureCanvas();
      }
      onExport([], captured);
    }, 300);
    return () => clearTimeout(timer);
  }, [open, autoExport, onExport]);

  // Reset autoExport trigger when editor closes
  useEffect(() => {
    if (!open) {
      autoExportTriggered.current = false;
    }
  }, [open]);

  if (!open) return null;

  // Auto-export mode: render only the PageCanvas offscreen for capture
  if (autoExport) {
    return (
      <Box sx={{ position: 'fixed', left: -9999, top: -9999, opacity: 0, pointerEvents: 'none' }}>
        <PageCanvas
          ref={pageCanvasRef}
          width={dimensions.width}
          height={dimensions.height}
          zoom={1}
          panOffset={{ x: 0, y: 0 }}
          onPanChange={() => {}}
          capturedCanvas={capturedCanvas}
          annotations={[]}
          selectedId={null}
          activeTool={null}
          onAnnotationAdd={() => {}}
          onAnnotationSelect={() => {}}
          onAnnotationMove={() => {}}
          onAnnotationResize={() => {}}
        />
      </Box>
    );
  }

  return (
    <Fade in={open}>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: '#1F2937',
          zIndex: 1300,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1,
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            backgroundColor: '#111827',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
              Annotation Editor
            </Typography>
            <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
              {paperSize} {orientation}
            </Typography>
          </Box>

          {/* Toolbar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Undo (Ctrl+Z)">
              <IconButton 
                size="small" 
                onClick={handleUndo}
                disabled={historyIndex <= 0}
                sx={{ color: 'rgba(255,255,255,0.7)' }}
              >
                <UndoIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Redo (Ctrl+Shift+Z)">
              <IconButton 
                size="small" 
                onClick={handleRedo}
                disabled={historyIndex >= history.length - 1}
                sx={{ color: 'rgba(255,255,255,0.7)' }}
              >
                <RedoIcon />
              </IconButton>
            </Tooltip>
            
            <Divider orientation="vertical" flexItem sx={{ mx: 1, borderColor: 'rgba(255,255,255,0.2)' }} />
            
            <Tooltip title="Zoom Out">
              <IconButton size="small" onClick={handleZoomOut} sx={{ color: 'rgba(255,255,255,0.7)' }}>
                <ZoomOutIcon />
              </IconButton>
            </Tooltip>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', minWidth: 50, textAlign: 'center' }}>
              {Math.round(zoom * 100)}%
            </Typography>
            <Tooltip title="Zoom In">
              <IconButton size="small" onClick={handleZoomIn} sx={{ color: 'rgba(255,255,255,0.7)' }}>
                <ZoomInIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Fit to Screen">
              <IconButton size="small" onClick={handleFitToScreen} sx={{ color: 'rgba(255,255,255,0.7)' }}>
                <FitScreenIcon />
              </IconButton>
            </Tooltip>
            
            <Divider orientation="vertical" flexItem sx={{ mx: 1, borderColor: 'rgba(255,255,255,0.2)' }} />
            
            {/* Save Menu */}
            <Button
              variant="outlined"
              startIcon={<SaveIcon />}
              endIcon={<KeyboardArrowDownIcon />}
              onClick={(e) => setSaveMenuAnchor(e.currentTarget)}
              sx={{ 
                textTransform: 'none',
                borderColor: 'rgba(255,255,255,0.3)',
                color: 'white',
                mr: 1,
                '&:hover': {
                  borderColor: 'rgba(255,255,255,0.5)',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                }
              }}
            >
              Save
            </Button>
            <Menu
              anchorEl={saveMenuAnchor}
              open={Boolean(saveMenuAnchor)}
              onClose={() => setSaveMenuAnchor(null)}
            >
              <MenuItem onClick={handleSaveProject} disabled={!onSaveProject}>
                <ListItemIcon>
                  <SaveIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Save Project</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => {
                setSaveMenuAnchor(null);
                setShowSaveTemplateDialog(true);
              }} disabled={!onSaveTemplate || annotations.length === 0}>
                <ListItemIcon>
                  <BookmarkIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Save as Template</ListItemText>
              </MenuItem>
            </Menu>
            
            <Button
              variant="contained"
              startIcon={isExporting ? <CircularProgress size={18} color="inherit" /> : <FileDownloadIcon />}
              onClick={handleExport}
              disabled={isExporting}
              sx={{ textTransform: 'none' }}
            >
              {isExporting ? 'Capturing...' : 'Export'}
            </Button>
            
            <IconButton onClick={onClose} sx={{ color: 'rgba(255,255,255,0.7)', ml: 1 }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Paper>
        
        {/* Save Template Dialog */}
        <Dialog
          open={showSaveTemplateDialog}
          onClose={() => setShowSaveTemplateDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Save as Template</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Save your current annotation layout as a reusable template.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              label="Template Name"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="e.g., Standard TGS Layout"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Description (optional)"
              value={templateDescription}
              onChange={(e) => setTemplateDescription(e.target.value)}
              placeholder="e.g., Title box top-right, legend bottom-left"
              multiline
              rows={2}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowSaveTemplateDialog(false)} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveTemplate}
              disabled={!templateName.trim() || savingTemplate}
              sx={{ textTransform: 'none' }}
            >
              {savingTemplate ? <CircularProgress size={20} /> : 'Save Template'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Main Content */}
        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Left Toolbar */}
          <AnnotationToolbar
            activeTool={null}
            onToolSelect={handleToolSelect}
            onApplyTemplate={handleApplyTemplate}
            templates={BUILT_IN_TEMPLATES}
          />

          {/* Center Canvas */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'auto',
              backgroundColor: '#374151',
            }}
          >
            <PageCanvas
              ref={pageCanvasRef}
              width={dimensions.width}
              height={dimensions.height}
              zoom={zoom}
              panOffset={panOffset}
              onPanChange={setPanOffset}
              capturedCanvas={capturedCanvas}
              annotations={annotations}
              selectedId={selectedId}
              activeTool={null}
              onAnnotationAdd={handleAddAnnotation}
              onAnnotationSelect={setSelectedId}
              onAnnotationMove={handleMoveAnnotation}
              onAnnotationResize={handleResizeAnnotation}
            />
          </Box>

          {/* Right Properties Panel */}
          <AnnotationPropertiesPanel
            annotation={selectedAnnotation}
            onUpdate={handleUpdateAnnotation}
            onDelete={handleDeleteAnnotation}
          />
        </Box>
      </Box>
    </Fade>
  );
};

export default AnnotationEditor;
